// /src/pages/Messages.js
import { useEffect, useState, useRef } from "react";
import { Send, MessageSquare, User, RefreshCw, ExternalLink } from "lucide-react";

function getToken() { return localStorage.getItem("token"); }

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full conversation object
  const [loadingConv, setLoadingConv] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState("messenger");
  const bottomRef = useRef(null);

  const loadConversations = () => {
    setLoading(true);
    fetch("/api/fbconversations", {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => res.json())
      .then(data => {
        setConversations(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadConversations(); }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selected?.messages]);

  const openConversation = async (conv) => {
    setLoadingConv(true);
    setSelected(null);
    try {
      const res = await fetch(`/api/fbconversations?psid=${conv.psid}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setSelected(data);
      // Update unread count in list
      setConversations(prev => prev.map(c =>
        c.psid === conv.psid ? { ...c, unread: 0 } : c
      ));
    } catch {}
    setLoadingConv(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    setSending(true);

    try {
      const res = await fetch("/api/fbmessengersend", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          psid: selected.psid,
          text: reply.trim(),
          conv_key: `conv:${selected.psid}`,
        }),
      });

      const data = await res.json();
      if (data.success) {
        // Optimistically add message to conversation
        setSelected(prev => ({
          ...prev,
          messages: [...prev.messages, {
            id: data.message_id || crypto.randomUUID(),
            from: "agent",
            text: reply.trim(),
            timestamp: Date.now(),
            read: true,
          }],
          last_message: reply.trim(),
          last_message_time: new Date().toISOString(),
        }));
        setReply("");
      } else {
        alert("Failed to send: " + (data.error || "unknown error"));
      }
    } catch {
      alert("Network error. Please try again.");
    }
    setSending(false);
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div className="page" style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <div className="page-title">Messages</div>
          <div className="page-subtitle">Facebook Messenger conversations</div>
        </div>
        <button className="btn btn-secondary" onClick={loadConversations} style={{ gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Channel tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
        {[
          { key: "messenger", label: "📘 Messenger", count: totalUnread },
          { key: "whatsapp", label: "💬 WhatsApp", count: 0 },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            fontWeight: activeTab === tab.key ? 700 : 400,
            color: activeTab === tab.key ? "#0f172a" : "#64748b",
            borderBottom: activeTab === tab.key ? "2px solid #0f172a" : "2px solid transparent",
            fontSize: 14, display: "flex", alignItems: "center", gap: 6,
          }}>
            {tab.label}
            {tab.count > 0 && (
              <span style={{ background: "#ef4444", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 11 }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "whatsapp" ? (
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", marginBottom: 8 }}>WhatsApp Coming Soon</div>
          <p style={{ color: "#64748b", fontSize: 14 }}>Set up a WhatsApp Business Account in Meta Business Suite to enable this channel.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, flex: 1, minHeight: 0 }}>

          {/* Conversation list */}
          <div className="card" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", fontWeight: 600, fontSize: 13, color: "#0f172a", flexShrink: 0 }}>
              Conversations {conversations.length > 0 && <span style={{ color: "#94a3b8", fontWeight: 400 }}>({conversations.length})</span>}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {loading ? (
                <p style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>Loading...</p>
              ) : conversations.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <MessageSquare size={32} color="#e2e8f0" style={{ marginBottom: 8 }} />
                  <p style={{ color: "#94a3b8", fontSize: 13 }}>No conversations yet.</p>
                  <p style={{ color: "#cbd5e1", fontSize: 12 }}>Messages from your Facebook Page will appear here.</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <div key={conv.psid} onClick={() => openConversation(conv)}
                    style={{
                      padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f8fafc",
                      background: selected?.psid === conv.psid ? "#f1f5f9" : "transparent",
                      transition: "background 0.1s",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <User size={16} color="#2563eb" />
                        </div>
                        <div>
                          <div style={{ fontWeight: conv.unread > 0 ? 700 : 500, fontSize: 13, color: "#0f172a" }}>
                            {conv.lead_name || "Unknown"}
                          </div>
                          {conv.lead_id && (
                            <div style={{ fontSize: 11, color: "#2563eb" }}>Linked to lead</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{timeAgo(conv.last_message_time)}</span>
                        {conv.unread > 0 && (
                          <span style={{ background: "#2563eb", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>
                            {conv.unread}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: 40 }}>
                      {conv.last_message || "No messages"}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Conversation view */}
          <div className="card" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {!selected && !loadingConv ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                <MessageSquare size={40} color="#e2e8f0" style={{ marginBottom: 12 }} />
                <p style={{ fontSize: 14 }}>Select a conversation to view messages</p>
              </div>
            ) : loadingConv ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                Loading conversation...
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
                      {selected.lead_name || "Unknown"}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      via Facebook Messenger
                      {selected.lead_id && (
                        <span style={{ marginLeft: 8, color: "#2563eb" }}>· Linked to lead</span>
                      )}
                    </div>
                  </div>
                  {selected.lead_id && (
                    <a href={`/leads`} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#2563eb", textDecoration: "none" }}>
                      <ExternalLink size={13} /> View Lead
                    </a>
                  )}
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {selected.messages.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center" }}>No messages yet</p>
                  ) : (
                    selected.messages.map((msg, i) => {
                      const isAgent = msg.from === "agent";
                      return (
                        <div key={msg.id || i} style={{ display: "flex", justifyContent: isAgent ? "flex-end" : "flex-start" }}>
                          <div style={{
                            maxWidth: "70%", padding: "10px 14px", borderRadius: isAgent ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                            background: isAgent ? "#0f172a" : "#f1f5f9",
                            color: isAgent ? "#fff" : "#0f172a",
                            fontSize: 14, lineHeight: 1.5,
                          }}>
                            {!isAgent && msg.agent_name && (
                              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{msg.agent_name}</div>
                            )}
                            {msg.text}
                            <div style={{ fontSize: 10, color: isAgent ? "#94a3b8" : "#cbd5e1", marginTop: 4, textAlign: "right" }}>
                              {timeAgo(typeof msg.timestamp === "number" ? new Date(msg.timestamp).toISOString() : msg.timestamp)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>

                {/* Reply box */}
                <form onSubmit={handleSend} style={{ padding: "12px 16px", borderTop: "1px solid #f1f5f9", display: "flex", gap: 10, flexShrink: 0 }}>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    placeholder="Type a message..."
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    disabled={sending}
                  />
                  <button type="submit" className="btn btn-primary" disabled={sending || !reply.trim()} style={{ gap: 6, flexShrink: 0 }}>
                    <Send size={14} />
                    {sending ? "Sending..." : "Send"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}